import ApiService from "../../ApiService";
import {
  ApiResponseMessage,
  ICreateSectionPayload,
  ICreateSectionResponse,
  ICreateSurveyFormPayload,
  ICreateSurveyFormQuestionPayload,
  IEditSectionResponse,
  IFormDetailsResponse,
  IFormResponsesResponse,
  IFormWithQuestionsResponse,
  IInvitedUsersResponse,
  ISectionListResponse,
  ISubmitSurveyFormResponsePayload,
  ISurveyFormsServiceResponse,
  IUserTypesResponse,
} from "../../../Types/SurveyFormsTypes";

enum SurveyFormsEndpoints {
  FORMS_LIST = "/survey/forms",
  ADD_FORM = "/admin/form-store",
  FORM_DETAILS = "/admin/form-question-list",
  FORM_QUESTION_ADD = "/admin/form/store-question",
  FORM_QUESTION_LIST = "/admin/form/add-question",
  FORM_RESPONSE_SUBMIT = "/admin/form/store/response",
  FORM_INVITED_USERS = "/admin/form-invited-users",
  FORM_USERS_ASSIGN = "/admin/form-invited-users",
  UPDATE_FORM_QUESTION_STATUS = "/admin/form-question/statusChange",
  UPDATE_FORM_STATUS = "/admin/form/statusChange",
  USER_TYPES_LIST = "/user/types-list",
  SECTION_LIST = "/sections-list",
  EDIT_SECTION = "/section",
  SAVE_SECTION = "/section/save",
  EDIT_SURVEY_FORM = "/survey-form/edit",
}

class SurveyFormsService extends ApiService {
  async fetchSurveyForms(
    userId?: number
  ): Promise<ISurveyFormsServiceResponse> {
    const url = userId
      ? `${SurveyFormsEndpoints.FORMS_LIST}/${userId}`
      : SurveyFormsEndpoints.FORMS_LIST;

    return this.get<ISurveyFormsServiceResponse>(url);
  }

  async getSurveyFormById(id: number): Promise<any> {
    return this.get<any>(`${SurveyFormsEndpoints.EDIT_SURVEY_FORM}/${id}`);
  }

  async submitSurveyForm(
    payload: ICreateSurveyFormPayload
  ): Promise<ApiResponseMessage> {
    return this.post<ApiResponseMessage>(
      SurveyFormsEndpoints.ADD_FORM,
      payload
    );
  }

  async getFormWithQuestions(
    form_id: number
  ): Promise<IFormWithQuestionsResponse> {
    return this.get(`${SurveyFormsEndpoints.FORM_QUESTION_LIST}/${form_id}`);
  }

  async submitSurveyFormQuestion(
    form_id: number,
    payload: ICreateSurveyFormQuestionPayload
  ): Promise<ApiResponseMessage> {
    return this.post<ApiResponseMessage>(
      `${SurveyFormsEndpoints.FORM_QUESTION_ADD}/${form_id}`,
      payload
    );
  }

  async getFormDetails(
    formId: number,
    userId?: number
  ): Promise<IFormDetailsResponse> {
    const url = userId
      ? `${SurveyFormsEndpoints.FORM_DETAILS}/${formId}/${userId}`
      : `${SurveyFormsEndpoints.FORM_DETAILS}/${formId}`;

    return this.get<IFormDetailsResponse>(url);
  }

  async submitSurveyFormResponse(
    form_id: number,
    user_id: number,
    payload: ISubmitSurveyFormResponsePayload
  ): Promise<ApiResponseMessage> {
    return this.post<ApiResponseMessage>(
      `${SurveyFormsEndpoints.FORM_RESPONSE_SUBMIT}/${user_id}/${form_id}`,
      payload
    );
  }

  async getInvitedUsersByFormId(
    formId: number
  ): Promise<IInvitedUsersResponse> {
    return this.get<IInvitedUsersResponse>(
      `${SurveyFormsEndpoints.FORM_INVITED_USERS}/${formId}`
    );
  }

  async assignInvitedUsers(
    formId: number,
    userIds: number[]
  ): Promise<ApiResponseMessage> {
    return this.post<ApiResponseMessage>(
      `${SurveyFormsEndpoints.FORM_USERS_ASSIGN}/${formId}`,
      { user_ids: userIds }
    );
  }

  async updateFormQuestionStatus(data: {
    question_id: number;
    status: number;
  }): Promise<ApiResponseMessage> {
    return this.post(
      `${SurveyFormsEndpoints.UPDATE_FORM_QUESTION_STATUS}/`,
      data
    );
  }

  async updateSurveyFormStatus(data: {
    form_id: number;
    status?: number;
    editable?: number;
  }): Promise<ApiResponseMessage> {
    return this.post(`${SurveyFormsEndpoints.UPDATE_FORM_STATUS}`, data);
  }

  //for roles
  // async fetchRoles(): Promise<IRolesResponse> {
  //   return this.get<IRolesResponse>(SurveyFormsEndpoints.ROLES_LIST);
  // }
  async fetchUserTypeList(): Promise<IUserTypesResponse> {
    return this.get<IUserTypesResponse>(SurveyFormsEndpoints.USER_TYPES_LIST);
  }
  //for sections
  async getSections(): Promise<ISectionListResponse> {
    return this.get<ISectionListResponse>(SurveyFormsEndpoints.SECTION_LIST);
  }

  async getEditSection(id: number): Promise<IEditSectionResponse> {
    return this.get<IEditSectionResponse>(
      `${SurveyFormsEndpoints.EDIT_SECTION}/${id}`
    );
  }

  async saveSection(
    payload: ICreateSectionPayload
  ): Promise<ICreateSectionResponse> {
    return this.post<ICreateSectionResponse>(
      SurveyFormsEndpoints.SAVE_SECTION,
      payload
    );
  }

  async getFormResponses(formId: number): Promise<IFormResponsesResponse> {
    return this.get<IFormResponsesResponse>(
      `/survey-forms/responses/${formId}`
    );
  }
}

export const surveyFormsService = new SurveyFormsService();
